<script setup>
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import ReportHeader from './components/ReportHeader.vue';
import SummaryReports from './components/SummaryReports.vue';
import TabelaCausaRaiz from './components/TabelaCausaRaiz.vue';
import AbasRelatorio from './components/AbasRelatorio.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';

// T-151 / T-150m / T-150n — "Tempo de atendimento": o mesmo conjunto de métricas, quebrado por
// agente, time, caixa de entrada ou causa raiz, num lugar só.
//
// T-254 (02/10): a tela se chamava "Quebras" e a quarta dimensão se chamava "etiqueta". Os dois
// nomes trocaram por decisão do clube na reunião de 01/10. O nome antigo era palavra nossa, e a
// Tamara disse o porquê do novo: "pode colocar como tempo de atendimento, porque é basicamente
// isso, é o tempo de primeira resposta, resolução".
//
// ⚠️ ATÉ 04/10 A QUARTA DIMENSÃO SÓ TINHA O NOME NOVO. Por baixo ela continuava agrupando pela
// ETIQUETA do Chatwoot, e o comentário aqui dizia que isso bastava "porque é onde a taxonomia é
// gravada". Estava errado, e a conferência de 04/10 mostrou por quê: a triagem aplica QUATRO
// naturezas de etiqueta ao mesmo tempo (sentimento, protocolo, tipo de atendimento e categoria),
// e a causa raiz não é nenhuma delas · são 203 valores, que não cabem em etiqueta, e por isso
// moram em `additional_attributes['fandata_causa_raiz']`.
//
// O resultado era uma tabela com linhas de quatro naturezas misturadas sob um título só, em que
// somar as linhas conta a mesma conversa até quatro vezes. Agora a dimensão é `causa_raiz`, com
// construtor e tabela próprios, e `?dim=label` continua chegando nela (ver `APELIDOS`).
//
// A reclamação dela foi "caixa de entrada, times e SLA se repetem", e a razão de repetirem é
// estrutural: as quatro páginas eram o MESMO componente com um agrupamento diferente. Quatro
// entradas de menu para uma pergunta só, que é "esse mesmo número, quebrado por quê?".
//
// Com o seletor aqui, comparar time com caixa vira um clique em vez de duas navegações e uma
// comparação de cabeça. E os dois itens que ela pediu saem de graça, porque passam a ser o mesmo
// recurso visto de outro ângulo: KPIs por agente (T-150m) e KPIs por causa raiz (T-150n).
//
// O SLA não sumiu: ele é métrica destas dimensões e um dos dez indicadores da Visão Geral. O que
// deixou de existir é a página que repetia a quebra por caixa e por time.
const { t } = useI18n();
const route = useRoute();

// Cada dimensão só troca quatro chaves de store. É por isso que quatro páginas viram uma.
const DIMENSOES = {
  agent: {
    actionKey: 'summaryReports/fetchAgentSummaryReports',
    getterKey: 'agents/getAgents',
    fetchItemsKey: 'agents/get',
    summaryKey: 'summaryReports/getAgentSummaryReports',
    download: 'AGENT_REPORTS.DOWNLOAD_AGENT_REPORTS',
  },
  team: {
    actionKey: 'summaryReports/fetchTeamSummaryReports',
    getterKey: 'teams/getTeams',
    fetchItemsKey: 'teams/get',
    summaryKey: 'summaryReports/getTeamSummaryReports',
    download: 'TEAM_REPORTS.DOWNLOAD_TEAM_REPORTS',
  },
  inbox: {
    actionKey: 'summaryReports/fetchInboxSummaryReports',
    getterKey: 'inboxes/getInboxes',
    fetchItemsKey: 'inboxes/get',
    summaryKey: 'summaryReports/getInboxSummaryReports',
    download: 'INBOX_REPORTS.DOWNLOAD_INBOX_REPORTS',
  },
  // T-254 (04/10): a quarta dimensão passou a ser a CAUSA RAIZ de verdade, com tabela própria.
  // Não tem chaves de store porque não vem do store: ver `proprio`, abaixo, e o comentário
  // grande no topo do arquivo.
  causa_raiz: {
    proprio: true,
    download: 'LABEL_REPORTS.DOWNLOAD_LABEL_REPORTS',
  },
};

// `?dim=label` é o endereço que o clube tem na mão, de quando a dimensão era a etiqueta. Continua
// chegando e cai na causa raiz, que é o que aquela aba sempre prometeu ser.
const APELIDOS = { label: 'causa_raiz' };

// As rotas antigas (agents_overview, teams_overview, ...) redirecionam para cá com ?dim=,
// então link antigo continua chegando na quebra certa.
const dimPedida = APELIDOS[route.query.dim] || route.query.dim;
const dimensaoInicial = Object.keys(DIMENSOES).includes(dimPedida)
  ? dimPedida
  : 'agent';
const dimensao = ref(dimensaoInicial);

const abas = computed(() =>
  Object.keys(DIMENSOES).map(key => ({
    key,
    label: t(`QUEBRAS.DIMENSOES.${key.toUpperCase()}`),
  }))
);

const config = computed(() => DIMENSOES[dimensao.value]);

const summaryRef = ref(null);
const onDownloadClick = () => summaryRef.value?.downloadReports();
</script>

<template>
  <!-- T-255 (02/10): a descrição saiu por pedido da Tamara, "pois é autoexplicativa". O
       seletor de dimensão logo abaixo já diz o que a tela faz.

       ⚠️ O comentário fica FORA da tag. Dentro, entre atributos, o compilador do Vue recusa
       ("Illegal '/' in tags") e o build quebra · foi o que derrubou o build de 04/10. O portão
       local não pega isso porque confere a sintaxe sem compilar. -->
  <ReportHeader :header-title="$t('QUEBRAS.HEADER')">
    <!-- A causa raiz tem o próprio botão de exportar, dentro da tabela: o download das outras
         dimensões sai do store do upstream, que não conhece esta quebra. -->
    <V4Button
      v-if="!config.proprio"
      :label="$t(config.download)"
      icon="i-ph-download-simple"
      size="sm"
      @click="onDownloadClick"
    />
  </ReportHeader>

  <AbasRelatorio v-model="dimensao" :abas="abas" />

  <!-- A `key` força a remontagem ao trocar de dimensão: sem ela, a tabela ficaria com as linhas
       da dimensão anterior enquanto a nova consulta não volta, e o número errado por um segundo
       é pior que a tabela vazia. -->
  <TabelaCausaRaiz v-if="config.proprio" :key="dimensao" />
  <SummaryReports
    v-else
    :key="dimensao"
    ref="summaryRef"
    :action-key="config.actionKey"
    :getter-key="config.getterKey"
    :fetch-items-key="config.fetchItemsKey"
    :summary-key="config.summaryKey"
    :type="dimensao"
  />
</template>
